import { type ComponentType, type ReactNode, type SVGProps, useLayoutEffect } from 'react';
import { create } from 'zustand';

// Icon component type for the header. Works for both SVGR-imported SVG
// components (CRA's `ReactComponent` import) and lucide icons, since both render
// as an <svg> that accepts standard SVG props.
export type PageHeaderIcon = ComponentType<SVGProps<SVGSVGElement>>;

// Richer app-shell header model, ported from student-hub's usePageHeader. Unlike
// hub's usePageTitle (a plain title string), this carries an icon + breadcrumb
// label and optional back button / right-hand slot. The app-shell header reads
// it; the layout sets it per-route from the page config (see page.ts).
//
// Note: student-hub stores `icon` as a string name resolved by <SvgIcon>. hub
// renders the icon component directly.
export interface PageHeaderContent {
  label: string;
  icon?: PageHeaderIcon;
  showIcon?: boolean;
  showBackButton?: boolean;
  headerRight?: ReactNode;
  // Overrides the back button's default history.goBack() (see AppShell PageHeader).
  // For pages that aren't a real route change, e.g. a step within a wizard.
  onBackClick?: () => void;
}

interface PageHeaderStore extends PageHeaderContent {
  // True while a page owns the header via useSetPageHeader; tells the app-shell
  // layout not to overwrite it with the static route config (see AppShell).
  owned: boolean;
  setHeader: (content: Partial<PageHeaderContent>) => void;
  setOwnedHeader: (content: PageHeaderContent) => void;
  resetHeader: () => void;
}

const EMPTY_HEADER: PageHeaderContent = {
  label: '',
  icon: undefined,
  showIcon: false,
  showBackButton: false,
  headerRight: undefined,
  onBackClick: undefined,
};

export const usePageHeader = create<PageHeaderStore>((set) => ({
  ...EMPTY_HEADER,
  owned: false,
  setHeader: (content) => set((state) => ({ ...state, ...content })),
  // Full replace (from EMPTY) + owned flag, so a stale field from a prior page
  // can't leak in and the layout knows to keep its hands off.
  setOwnedHeader: (content) => set({ ...EMPTY_HEADER, ...content, owned: true }),
  resetHeader: () => set({ ...EMPTY_HEADER, owned: false }),
}));

// Sets the richer app-shell header for the lifetime of the calling component and
// clears it on unmount. Lets a page own a dynamic header (e.g. a fetched student
// name) the static page.ts config can't express.
//
// Runs as a layout effect (not passive) and marks the header `owned` so it beats
// — and, critically, isn't clobbered by — the app-shell layout's own route-driven
// header effect regardless of mount/unmount ordering. (A passive cleanup here
// used to fire after the next page's layout effect and wipe its header.)
export function useSetPageHeader(content: PageHeaderContent) {
  const setOwnedHeader = usePageHeader((state) => state.setOwnedHeader);
  const resetHeader = usePageHeader((state) => state.resetHeader);
  const { label, icon, showIcon = false, showBackButton = false, headerRight, onBackClick } = content;

  useLayoutEffect(() => {
    setOwnedHeader({ label, icon, showIcon, showBackButton, headerRight, onBackClick });
    return () => resetHeader();
  }, [label, icon, showIcon, showBackButton, headerRight, onBackClick, setOwnedHeader, resetHeader]);
}
